// Display rules for the naval item categories (doubloons, shipXp,
// blueprintsChest, shipBlueprint).
//
// Kept free of Svelte and of the stores so it can be unit-tested with
// `node --test src/lib/Items/naval-items.test.js`, the same way tab-interest.js
// and advent-variant.js are.
//
// Every rule here mirrors the Unity client's Item* models, which are the
// reference implementation for what these items look like. See
// docs/superpowers/specs/2026-08-13-naval-item-renderers-design.md.

/**
 * Sprite class names, matching the atlas classes in styles/atlases/GameItems.css.
 *
 * Every name here must exist in the atlas: one that resolves to nothing renders as
 * a white box rather than as nothing (SC-4508).
 *
 * doubloons and shipXp mirror Unity's ItemDoubloons.DoubloonsSpriteName and
 * ItemShipXp.ShipXpSpriteName, which moved off the reused gold/Lord-XP art in
 * dev@3db62d8732 once real art existed. Two warnings carried over from there:
 * doubloons must NOT go back to `gold_l`, which ~25 unrelated places use for
 * actual gold (the two currencies drew identically); and shipXp uses the regular
 * `naval_exp_icon`, not `naval_exp_icon_large`, because every surface that draws
 * it is an inventory-cell-sized slot.
 *
 * blueprintsChest and shipBlueprint stay placeholder FALLBACKS. Per-size chest art
 * and per-ship blueprint art landed on 2026-08-20 (dev@4d879c960e and
 * dev@3db62d8732), so a chest size or ship we recognise uses its own sprite — see
 * blueprintsChestIcon() and shipBlueprintIcon().
 */
export const NAVAL_ICONS = {
    doubloons: 'doubloon_icon',
    shipXp: 'naval_exp_icon',
    blueprintsChest: 'blueprint_item_placeholder',
    shipBlueprint: 'blueprint_item_placeholder',
};

/**
 * Chest sizes that have real art in the atlas. The server `type` doubles as the
 * sprite name for these — the atlas classes are literally `.blueprintsChestSmall`
 * and friends — but only for these. An unrecognised size (a new one added
 * server-side before its art ships) must fall back to the placeholder rather than
 * resolve to nothing, which renders a white box (SC-4508).
 *
 * Listed explicitly rather than pattern-matched on the `blueprintsChest` prefix
 * for exactly that reason: a prefix test would happily pass through
 * "blueprintsChestGigantic" and render the white box this guards against.
 */
const BLUEPRINTS_CHEST_SPRITES = new Set([
    'blueprintsChestSmall',
    'blueprintsChestMedium',
    'blueprintsChestLarge',
    'blueprintsChestExtraLarge',
]);

export function blueprintsChestIcon(type) {
    return BLUEPRINTS_CHEST_SPRITES.has(type) ? type : NAVAL_ICONS.blueprintsChest;
}

/**
 * North for side 1, South for anything else — the convention already used by
 * SystemMessage.svelte. Ship names differ by faction ("USS Harriet Lane" vs
 * "CSS Nashville"), so this decides which name a player sees.
 */
export function sideSuffix(side) {
    return side === 1 ? 'North' : 'South';
}

/**
 * Side to draw ART for. South only when the player is explicitly South; None (0),
 * North and a missing side all read as North.
 *
 * Deliberately NOT sideSuffix(), which sends everything that is not North to
 * South. That is right for localization — Unity's PlayerSideSuffix returns null
 * for sideless so the loc chain falls through to a side-neutral term — but art has
 * no neutral variant, so a sideless player must get a real faction rather than the
 * wrong one. Mirrors ItemShipBlueprint.ArtSideSuffix() in the Unity client, and
 * matches the rule its ship portraits, battle models and commander art all use.
 */
export function artSideSuffix(side) {
    return side === 2 ? 'South' : 'North';
}

export function doubloonKeys() {
    return { title: 'doubloonsItemTitle', description: 'doubloonsItemDescription' };
}

/**
 * Doubloons carried by one item: the catalog `amount`, or 1 when the entry
 * declares none (the live entry does not).
 *
 * Must never fall back to `goldValue`. SC-4547: a hardcoded x50 here made ship
 * upgrades look affordable that the server then rejected with "Not enough items".
 */
export function doubloonValue(gameItem) {
    const amount = Number(gameItem?.amount);
    return Number.isFinite(amount) && amount > 0 ? amount : 1;
}

// Each builder returns nulls when the field its key is built from is unusable,
// so callers never look up a key like "shipXpNaNTitle" or "undefinedTitle".
const NO_KEYS = { title: null, description: null };

export function shipXpKeys(amount) {
    if (!Number.isFinite(amount)) return NO_KEYS;
    return { title: `shipXp${amount}Title`, description: `shipXp${amount}Description` };
}

export function blueprintsChestKeys(type) {
    if (!type) return NO_KEYS;
    return { title: `${type}Title`, description: `${type}Description` };
}

/**
 * Ships with real blueprint art in the atlas, as `ship_{baseId}_{side}_blueprint`
 * — the same (baseId, side) pair shipBlueprintKeys() builds its i18n rows from.
 *
 * Enumerated for the same reason as BLUEPRINTS_CHEST_SPRITES: art exists for
 * ships 1-5 only, and a sprite name that resolves to nothing renders a white box
 * (SC-4508). A ship added server-side before its art ships keeps the placeholder.
 */
const SHIP_BLUEPRINT_SPRITES = new Set([
    'ship_1_North_blueprint',
    'ship_1_South_blueprint',
    'ship_2_North_blueprint',
    'ship_2_South_blueprint',
    'ship_3_North_blueprint',
    'ship_3_South_blueprint',
    'ship_4_North_blueprint',
    'ship_4_South_blueprint',
    'ship_5_North_blueprint',
    'ship_5_South_blueprint',
]);

export function shipBlueprintIcon(baseId, side) {
    if (!Number.isFinite(baseId)) return NAVAL_ICONS.shipBlueprint;
    const name = `ship_${baseId}_${side}_blueprint`;
    return SHIP_BLUEPRINT_SPRITES.has(name) ? name : NAVAL_ICONS.shipBlueprint;
}

export function shipBlueprintKeys(baseId, side) {
    if (!Number.isFinite(baseId)) return NO_KEYS;
    return {
        title: `ship${baseId}_blueprintTitle${side}`,
        description: `ship${baseId}_blueprintDescription${side}`,
    };
}

/**
 * Translate `key`, or return `fallback` when the row is not usable.
 *
 * Deliberately does NOT consult a list of which rows are authored. svelte-i18n
 * echoes the key back for a missing row, so a result equal to the key counts as
 * missing — otherwise a player would see "shipXp7000Title" in the pack list.
 * Detecting it this way means a row authored later starts being used with no
 * code change, where a hardcoded set of "known" ids would keep showing the
 * fallback until someone remembered to update it.
 *
 * A null/empty key means the item data lacked the field the key is built from
 * (no chest `type`, a non-numeric amount), so there is nothing worth looking up.
 */
export function resolveTitle({ key, fallback, translate }) {
    if (!key) return fallback;

    let value;
    try {
        value = translate(key);
    } catch {
        return fallback;
    }

    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === key) return fallback;
    return value;
}
